import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { auth } from "../lib/auth";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const INTRO_SEEN_KEY = "omnimark_intro_seen";
const INTRO_VIDEO_SRC = "/videos/opening.mp4";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showIntro, setShowIntro] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(INTRO_SEEN_KEY) !== "true";
  });
  const navigate = useNavigate();

  function finishIntro() {
    localStorage.setItem(INTRO_SEEN_KEY, "true");
    setShowIntro(false);
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#070b16] text-white">
      {showIntro && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black p-2 sm:p-4">
          <video
            className="h-full max-h-screen w-full max-w-screen object-contain"
            src={INTRO_VIDEO_SRC}
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={finishIntro}
            onError={finishIntro}
          />
          <Button
            type="button"
            variant="secondary"
            className="absolute right-4 top-4"
            onClick={finishIntro}
          >
            Skip
          </Button>
        </div>
      )}
      <video
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-35"
        src={INTRO_VIDEO_SRC}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(20,184,166,0.26),transparent_32%),radial-gradient(circle_at_82%_18%,rgba(124,58,237,0.32),transparent_34%),linear-gradient(135deg,rgba(7,11,22,0.72),rgba(7,11,22,0.96))]" />

      <main className="relative z-10 grid min-h-screen grid-cols-1 items-center gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[minmax(0,1fr)_440px] lg:px-12">
        <section className="mx-auto grid w-full max-w-3xl gap-6">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-2xl border border-white/15 bg-white/10 text-sm font-black shadow-2xl shadow-cyan-500/20">
              OM
            </div>
            <div>
              <div className="text-lg font-black tracking-tight">OmniMark HQ</div>
              <div className="text-sm text-white/60">AI Marketing Platform</div>
            </div>
          </div>

          <div className="max-w-2xl">
            <div className="mb-3 w-fit rounded-full border border-emerald-300/20 bg-emerald-300/10 px-3 py-1 text-xs font-black uppercase tracking-normal text-emerald-100">
              AI command center for brands, ads, stores, and dropshipping
            </div>
            <h1 className="text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
              Sign in to your AI marketing headquarters.
            </h1>
            <p className="mt-4 max-w-xl text-base leading-7 text-white/70">
              Build campaigns, generate avatar-led videos, scan products, connect stores, and let OmniMark AI help move every workspace forward.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {[
              ["AI Studio", "Camera-ready videos and avatar scripts"],
              ["Dropshipping Lab", "Product scans, test pages, supplier signals"],
              ["Live Growth", "Ads, sales, inventory, and AI recommendations"],
            ].map(([title, text]) => (
              <div key={title} className="rounded-lg border border-white/10 bg-white/[0.07] p-4 backdrop-blur">
                <div className="font-black">{title}</div>
                <div className="mt-1 text-sm text-white/60">{text}</div>
              </div>
            ))}
          </div>
        </section>

        <Card className="w-full border-white/10 bg-white/95 text-slate-950 shadow-2xl shadow-black/40 backdrop-blur">
          <CardHeader>
            <CardTitle>Welcome back</CardTitle>
            <p className="text-sm text-muted-foreground">
              Enter your email to access OmniMark HQ.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs leading-5 text-blue-950">
              By signing in, you understand and agree to OmniMark HQ's{" "}
              <Link to="/terms" className="font-bold underline">Terms of Service</Link>,{" "}
              <Link to="/privacy" className="font-bold underline">Privacy Policy</Link>, and{" "}
              <Link to="/refund" className="font-bold underline">Refund Policy</Link>.
            </div>
            <Input
              type="email"
              autoComplete="email"
              placeholder="you@domain.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
            />
            <Input
              type="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            {notice && <p role="status" className="text-sm text-emerald-700">{notice}</p>}
            <Button
              type="button"
              className="w-full"
              data-testid="login-submit-btn"
              disabled={submitting}
              onClick={async () => {
                if (!email.includes("@") || !password) return setError("Enter your email and password.");
                setSubmitting(true);
                setError("");
                setNotice("");
                try {
                  const activeSession = await auth.signIn(email, password);
                  // Seed the API client before /app mounts. This is the
                  // authoritative bearer used by the backend for every
                  // protected request; the interceptor continues to refresh
                  // it for later requests.
                  api.defaults.headers.common.Authorization = `Bearer ${activeSession.access_token}`;
                  navigate("/app", { replace: true, state: { justSignedIn: true } });
                } catch (reason) {
                  setError(reason.message || "Unable to sign in.");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              <button
                type="button"
                disabled={submitting}
                className="hover:text-slate-950"
                onClick={async () => {
                  if (!email.includes("@")) return setError("Enter your account email first, then choose Forgot password.");
                  setSubmitting(true);
                  setError("");
                  setNotice("");
                  try {
                    await auth.requestPasswordRecovery(email);
                    setNotice("If that email has an OmniMark account, a secure password-reset link is on its way.");
                  } catch (reason) {
                    setError(reason.message || "Unable to send a recovery email.");
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                Forgot password?
              </button>
              <Link to="/contact" className="hover:text-slate-950">Need help?</Link>
              <button type="button" onClick={() => setShowIntro(true)} className="hover:text-slate-950">
                Replay intro video
              </button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
